/**
 * Websocket configuration for Schedule Exchange (Power page)
 *
 * This file defines handlers for incoming websocket messages and manages the websocket connection
 * via RTK Query in powerApi.ts (getWebsocketMessages endpoint).
 *
 * NOTE: Currently there's an over-engineering issue where the websocket gets set up twice:
 * - Once via setSelectedResource async thunk manually calling .initiate()
 * - Once via useGetWebsocketMessagesQuery hook auto-subscribing when selectedResource changes
 * See power.actions.ts and Power.tsx for TODO comments about simplifying this.
 */
import { dateTimeRangeTypes } from "@arenko-group/arenko-ui/dist/enums";
import { createEntityAdapter } from "@reduxjs/toolkit";
import { setSchedulesDataLoading } from "@/features/powerRootSlice";
import { setLiveRange } from "@/features/powerRootSlice";
import {
  setOutputs,
  setSelectedOutput,
} from "@/features/powerRootSlice/powerSlice";
import {
  setSelectedRange,
  setTypes,
} from "@/features/powerRootSlice/powerSlice";
import { schedulePowerApi } from "@/features/powerRootSlice/services/powerApi";
import {
  extraneousFSPlot,
  getEmptyScheduleObject,
} from "@/helpers/chart.helpers";
import { getUniqueIdFromAbsolutes } from "@/helpers/general.helpers";
import type { RootState } from "@/store";
import type { YAxisRangeType } from "@/types/common/websockets.types";
import type {
  DispatchFn,
  DisplayAsObject,
  SelectedOutputObj,
  WebsocketMessageDraftType,
} from "@/types/data/redux.types";
import type {
  PlotData,
  Profile,
  Schedule,
  WebsocketDataType,
} from "@/types/data/schedule.types";

export const url = "schedule-exchange";

export const websocketAdapters = {
  types: createEntityAdapter({
    selectId: (message: { name: string }) => message.name,
  }),
  scheduleData: createEntityAdapter({
    selectId: (schedule: PlotData) => schedule.uniqueId,
  }),
  resultants: createEntityAdapter({
    selectId: (resultant: Record<string, Profile[]>) => {
      const values: Profile[] = Object.values(resultant)[0];
      return `${new Date(values[0].time).getTime()}${new Date(
        values[values.length - 1].time
      ).getTime()}`;
    },
  }),
};

type WebsocketDataHandler = {
  range: ({
    data,
    draft,
  }: {
    data: YAxisRangeType;
    draft: WebsocketMessageDraftType;
  }) => void;
  newPlotData: ({
    data,
    draft,
  }: {
    data: WebsocketDataType;
    draft: WebsocketMessageDraftType;
  }) => void;
  view: ({
    draft,
    data,
  }: {
    draft: WebsocketMessageDraftType;
    data: WebsocketDataType;
  }) => void;
  live: ({
    data,
    draft,
    state,
  }: {
    data: WebsocketDataType;
    draft: WebsocketMessageDraftType;
    state: RootState;
  }) => void;
};
export type WebsocketMsgType = keyof WebsocketDataHandler;

//keys must match message types
export const websocketDataHandler: WebsocketDataHandler = {
  range: ({ draft, data }) => (draft.range = data),
  live: ({ draft, data, state }) => {
    if (
      state?.power?.schedule?.selectedRange?.type === dateTimeRangeTypes.live
    ) {
      websocketDataHandler.newPlotData({ draft, data });
    }
  },
  view: ({ draft, data }) => {
    websocketDataHandler.newPlotData({ draft, data });
  },
  newPlotData: ({ draft, data }) => {
    //to make sure resultants us always up to date, use set
    websocketAdapters.resultants.setOne(draft.resultants, data.resultants);

    data.plotSchedules.forEach((plot: Schedule, i: number) => {
      const absolutes = data.absolutes?.[i];

      if (extraneousFSPlot(plot.type, absolutes)) return;
      const bases = data.bases?.[i];
      const emptyScheduleObj = getEmptyScheduleObject();
      const uniqueId = getUniqueIdFromAbsolutes(plot.id, absolutes);
      websocketAdapters.scheduleData.upsertOne(draft.scheduleData, {
        uniqueId,
        id: plot.id,
        service: plot.type,
        output: plot.output,
        state: plot.state.state,
        origin: plot.origin,
        created: plot.createdAt,
        profiles: plot.profile,
        absolutes,
        bases,
        timeRange: {
          from: new Date(plot.profile[0].time).getTime(),
          to: new Date(plot.profile[plot.profile.length - 1].time).getTime(),
        },
        correspondingSchedule:
          data.schedules.find((s: Schedule) => s.id === plot.id) ??
          emptyScheduleObj,
      });
    });
  },
};

type WebsocketDispatchActionHandler = {
  types: ({
    data,
    dispatch,
  }: {
    data: DisplayAsObject[];
    dispatch: DispatchFn;
  }) => void;
  outputs: ({
    data,
    dispatch,
    state,
  }: {
    data: SelectedOutputObj[];
    dispatch: DispatchFn;
    state: RootState;
  }) => void;
  view: ({ dispatch }: { dispatch: DispatchFn }) => void;
  live: ({
    data,
    dispatch,
    state,
  }: {
    data: WebsocketDataType;
    dispatch: DispatchFn;
    state: RootState;
  }) => void;
};
export type WebSocketActionType = keyof WebsocketDispatchActionHandler;

export const websocketDispatchActionHandler: WebsocketDispatchActionHandler = {
  types: ({ data, dispatch }) => {
    dispatch(setTypes(data));
  },
  outputs: ({ data, dispatch, state }) => {
    dispatch(setOutputs(data));
    if (state?.power?.schedule?.selectedOutput) return;
    dispatch(setSelectedOutput(data[0].key));
  },
  view: ({ dispatch }) => {
    dispatch(setSchedulesDataLoading(false));
  },
  live: ({ data, dispatch, state }) => {
    dispatch(setLiveRange(data.timeRange));
    //if on live, the selected date range needs to be updated
    if (
      state?.power?.schedule?.selectedRange?.type === dateTimeRangeTypes.live
    ) {
      if (
        state?.power?.schedule?.selectedRange?.from !== data?.timeRange?.from
      ) {
        dispatch(
          setSelectedRange({
            type: dateTimeRangeTypes.live,
            ...data.timeRange,
          })
        );
      }
      dispatch(setSchedulesDataLoading(false));
    } else {
      //live fires after a new schedule is added so use this as a sign to re-ask for the current requested schedule
      dispatch(
        schedulePowerApi.endpoints.requestData.initiate("schedex-noreload")
      );
    }
  },
};

export const initialState = {
  types: websocketAdapters.types.getInitialState(),
  range: undefined,
  scheduleData: websocketAdapters.scheduleData.getInitialState(),
  resultants: websocketAdapters.resultants.getInitialState(),
};
